/**
 * The menu: the desktop sidebar, its rail, and the touch tier's drawer —
 * TRBUD-439, design v6 (frames A, B, C and D).
 *
 * One component in three states, so the group toggles, the page search and the
 * account row are identical wherever they appear:
 *
 * - **Expanded** (desktop, the default above 1099px): five groups, a "Find a
 *   page" search, a pinned foot holding the account row and "Collapse menu".
 * - **Rail** (desktop collapsed, and the touch tier's default from 700 to
 *   1099px): an icon per destination, groups split by a hairline.
 * - **Drawer** (the touch tier expanded): the expanded sidebar as an overlay at
 *   the float level over a dimmed page, closed from a button beside its search,
 *   with only the current page's group open. An overlay rather than a push,
 *   because pushing would re-wrap every two-up screen each time the menu opens.
 *
 * Permissions and device policy stay in `@truckbuddy/contract`'s navigation
 * table; this file only draws what that table lets a person see.
 *
 * Counts (TRBUD-443): Requests, Review queue and Attendance review carry what
 * waits on them, from `GET /nav-counts` (`useNavCounts`, called once by the
 * shell). A number after the label when the menu is open, a dot on the rail,
 * and a dot on a closed group that hides a counted entry. Nothing is drawn at
 * zero, before the counts load, or when the read failed, and the server sends
 * `null` for a page the person cannot open, so no number ever sits on an entry
 * whose page would refuse them.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  activeNavigationPath, navCountFor, navCountName, navCountText, navigationGroups,
  type Capabilities, type NavCounts, type UserRole,
} from "@truckbuddy/contract";
import { useIsTouchLayout } from "../lib/useIsTouchLayout";
import { AccountMenu } from "./AccountMenu";
import { NavIcon, iconForPath } from "./NavIcons";
import { FOCUSABLE } from "./Dialog";

/** Reports is the one group that starts closed off its own pages (design v2 #8). */
const CLOSED_BY_DEFAULT = new Set(["reports"]);

function readJson(key: string): Record<string, boolean> {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(key) ?? "{}");
    if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>).filter((entry): entry is [string, boolean] => typeof entry[1] === "boolean"),
    );
  } catch {
    return {};
  }
}

function writeJson(key: string, value: Record<string, boolean>): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* Preference storage is optional; the menu still works for this visit. */
  }
}

type MenuMode = "expanded" | "rail" | "drawer";

/** Current-branch navigation only; existing permissions and compact device policy remain authoritative. */
export function AppWorkspace({ roles, can, pathname, viewerKey, counts = null, children }: {
  roles: readonly UserRole[]; can: Capabilities; pathname: string; viewerKey: string;
  /** What waits on the counted pages; null until loaded or after a failed read. */
  counts?: NavCounts | null;
  children: ReactNode;
}) {
  const touchTier = useIsTouchLayout();
  const { search: pageSearch } = useLocation();
  const collapsedKey = `truckbuddy.navigation.collapsed.${viewerKey}`;
  const groupsKey = `truckbuddy.navigation.groups.${viewerKey}`;
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(collapsedKey) === "true"; } catch { return false; }
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [query, setQuery] = useState("");
  // The person's own open and closed choices on the desktop menu, remembered per user.
  const [groupPrefs, setGroupPrefs] = useState<Record<string, boolean>>(() => readJson(groupsKey));
  // The drawer starts with only the current page's group open, every time it opens.
  const [drawerGroups, setDrawerGroups] = useState<Record<string, boolean>>({});
  const searchInput = useRef<HTMLInputElement>(null);
  const opener = useRef<HTMLButtonElement>(null);
  const sidebar = useRef<HTMLElement>(null);
  const drawerOpener = useRef<HTMLElement | null>(null);
  const focusSearch = useRef(false);
  const restoreFocus = useRef(false);

  const mode: MenuMode = touchTier ? (drawerOpen ? "drawer" : "rail") : collapsed ? "rail" : "expanded";
  const rail = mode === "rail";
  const groups = navigationGroups(roles, can, rail ? "" : query);
  const activePath = activeNavigationPath(groups.flatMap((group) => group.items), pathname);

  useEffect(() => {
    try { localStorage.setItem(collapsedKey, String(collapsed)); } catch { /* Preference storage is optional. */ }
  }, [collapsed, collapsedKey]);

  // Into the search when the menu opens from the rail's search button, or when
  // the drawer opens; back to the control that opened the drawer when it closes.
  useEffect(() => {
    if (mode !== "rail" && focusSearch.current) {
      searchInput.current?.focus();
      focusSearch.current = false;
    }
    if (mode === "rail" && restoreFocus.current) {
      restoreFocus.current = false;
      (drawerOpener.current?.isConnected ? drawerOpener.current : opener.current)?.focus();
    }
  }, [mode]);

  // Leaving the touch tier with the drawer open would strand an overlay on a desktop.
  useEffect(() => {
    if (!touchTier) setDrawerOpen(false);
  }, [touchTier]);

  useEffect(() => {
    if (mode !== "drawer") return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeDrawer();
      if (event.key === "Tab") {
        const controls = sidebar.current?.querySelectorAll<HTMLElement>(FOCUSABLE);
        const first = controls?.[0];
        const last = controls?.[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  });

  function openDrawer(withSearch: boolean) {
    drawerOpener.current = document.activeElement instanceof HTMLElement ? document.activeElement : opener.current;
    const active = groups.find((group) => group.items.some((item) => item.path === activePath));
    setDrawerGroups(active ? { [active.id]: true } : {});
    focusSearch.current = true;
    if (!withSearch) setQuery("");
    setDrawerOpen(true);
  }

  function closeDrawer() {
    restoreFocus.current = true;
    setQuery("");
    setDrawerOpen(false);
  }

  function isOpen(groupId: string, hasActive: boolean): boolean {
    if (rail || query.trim() !== "") return true;
    if (mode === "drawer") return drawerGroups[groupId] ?? false;
    return (groupId === "reports" && hasActive) || (groupPrefs[groupId] ?? !CLOSED_BY_DEFAULT.has(groupId));
  }

  function toggle(groupId: string, open: boolean) {
    if (mode === "drawer") {
      setDrawerGroups((current) => ({ ...current, [groupId]: !open }));
      return;
    }
    setGroupPrefs((current) => {
      const next = { ...current, [groupId]: !open };
      writeJson(groupsKey, next);
      return next;
    });
  }

  const asideClass = `app-sidebar app-sidebar-${mode}`;
  return <div className={`app-workspace app-workspace-${mode === "drawer" ? "rail" : mode}${touchTier ? " app-workspace-touch" : ""}`}>
    {mode === "drawer" && <>
      <div className="app-rail-ghost" aria-hidden="true" />
      <div className="app-drawer-backdrop" aria-hidden="true" onClick={closeDrawer} />
    </>}
    <aside ref={sidebar} className={asideClass} aria-label="Application sidebar"
      {...(mode === "drawer" ? { role: "dialog", "aria-modal": true } : {})}>
      <div className="app-sidebar-top">
        {rail ? <button type="button" className="app-sidebar-search-button" aria-label="Expand navigation search" title="Find a page"
          onClick={() => {
            if (touchTier) { openDrawer(true); return; }
            focusSearch.current = true;
            setCollapsed(false);
          }}>
          <NavIcon name="search" />
        </button> :
          <label className="app-sidebar-search">
            <span className="sr-only">Search navigation</span>
            <NavIcon name="search" />
            <input ref={searchInput} type="search" className="input" aria-label="Search navigation" placeholder="Find a page" maxLength={100}
              value={query} onChange={(event) => setQuery(event.currentTarget.value)} />
          </label>}
        {mode === "drawer" && <button type="button" className="app-sidebar-icon-button app-drawer-close" aria-label="Close menu" title="Close menu"
          onClick={closeDrawer}>
          <NavIcon name="close" />
        </button>}
      </div>
      <nav className="app-sidebar-pages" aria-label="Workspace navigation">
        {groups.map((group) => {
          const hasActive = group.items.some((item) => item.path === activePath);
          const open = isOpen(group.id, hasActive);
          const id = `app-nav-${group.id}`;
          // A group of one is drawn as the item alone (design v1 (a)): a head
          // over a single row is a heading with no job. The metadata keeps it.
          const bare = group.items.length === 1;
          // A closed group says when one of the entries it hides has something waiting.
          const hiddenWaiting = !open && group.items.some((item) => navCountFor(item.path, counts) !== null);
          return <section className={`app-sidebar-group${bare ? " app-sidebar-group-bare" : ""}`} key={group.id}
            title={rail ? group.label : undefined}>
            {!rail && !bare &&
              <button className="app-sidebar-group-toggle" type="button" aria-expanded={open} aria-controls={id}
                onClick={() => toggle(group.id, open)}>
                <NavIcon name="chevron-down" className="app-sidebar-chevron" />
                <span className="app-sidebar-group-label">{group.label}</span>
                {/* The count says how much is hidden; an open group needs none. */}
                {!open && <span className="app-sidebar-count">{group.items.length}</span>}
                {hiddenWaiting && <>
                  <span className="app-nav-dot app-nav-dot-inline" aria-hidden="true" />
                  <span className="sr-only">, items waiting</span>
                </>}
              </button>}
            {(open || bare) && <div id={id} className="app-sidebar-items">{group.items.map((item) => {
              const count = navCountFor(item.path, counts);
              const name = navCountName(item.label, count);
              return <Link key={item.path} to={item.group === "reports" && pathname.startsWith("/reports") ? `${item.path}${pageSearch}` : item.path} title={name} aria-label={name}
                aria-current={activePath === item.path ? "page" : undefined} className="app-sidebar-link"
                onClick={() => { if (mode === "drawer") closeDrawer(); }}>
                <NavIcon name={iconForPath(item.path)} />
                <span className="app-sidebar-link-label">{item.label}</span>
                {/* The name already carries the count, so the drawn mark is hidden from it. */}
                {count !== null && (rail
                  ? <span className="app-nav-dot" aria-hidden="true" />
                  : <span className="app-nav-count" aria-hidden="true">{navCountText(count)}</span>)}
              </Link>;
            })}</div>}
          </section>;
        })}
        {groups.length === 0 && <div className="app-sidebar-empty" role="status"><p>No pages match “{query}”.</p>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setQuery("")}>Clear navigation search</button></div>}
      </nav>
      <div className="app-sidebar-foot">
        <AccountMenu />
        {mode === "expanded" && <button type="button" className="app-sidebar-icon-button app-sidebar-collapse"
          aria-label="Collapse menu" title="Collapse menu" onClick={() => setCollapsed(true)}>
          <NavIcon name="panel" />
        </button>}
        {mode === "rail" && <button ref={opener} type="button" className="app-sidebar-icon-button app-sidebar-collapse"
          aria-label={touchTier ? "Open menu" : "Expand menu"} title={touchTier ? "Open menu" : "Expand menu"}
          onClick={() => { if (touchTier) openDrawer(false); else setCollapsed(false); }}>
          <NavIcon name="chevron-right" />
        </button>}
      </div>
    </aside>
    <div className="app-workspace-main">{children}</div>
  </div>;
}
